import { API_BASE_URL } from '../config';

/**
 * User Management API Endpoints
 * Framework-agnostic endpoint definitions
 */

// Role Endpoints
export const ROLE_ENDPOINTS = {
  GET_ALL: 'user-role/list-roles',
  CREATE: 'user-role/create-user-role', // Updated to match superadmin API
  UPDATE: 'user-role/update-role', // Updated to use update-role API
  DELETE: 'user-role/delete-role',
  GET_PARENT_ROLES: 'user-role/parentroles',
  GET_HIERARCHY: 'user-role/role-hierarchy',
  GET_DYNAMIC_FIELDS: 'user-role/get-table-fields-bygroup',
};

// Role Settings Endpoints
export const ROLE_SETTINGS_ENDPOINTS = {
  GET_CONFIGURATIONS: 'user-role/get-rolefields', // ?role_id=X&program_id=1
  UPDATE_CONFIGURATIONS: 'user-role/update-role',
  GET_DYNAMIC_FIELDS: 'user-role/get-table-fields-bygroup', // ?feature_short_name=RL&program_id=1
  GET_ROLE_HIERARCHY: 'user-role/get-parent-child-roles', // ?role_id=X&program_id=1
  GET_ALL_MODULES: 'user-modules/get-modules-lists',
};

// User Endpoints
export const USER_ENDPOINTS = {
  GET_GEO_GROUPS_BY_ROLE: 'user-group/list-user-groups-by-role',
  GET_ALL: 'users/get-users', // With query params: page, search_string, geographical_id, user_role_id, state_code, region_id, city
  // GET_USER_DETAILS: 'users/get-userdetails', // ?role_id=X
  GET_USER_DETAILS: 'users/list-users', // ?program_id=1&role_id=X&page=1&page_size=20&mobile=
  LIST_BLOCK_USERS: 'users/list-block-users', // GET ?program_id=1&list_status=&page=1&page_size=20 (list_status: 1 pending | 2 approved | 3 rejected | empty all)
  UPDATE_USER_STATUS: 'users/updateuserstatus', // POST FormData: user_id, action_status (1 Active | 2 Temp block | 3 Waiting approval | 4 Permanent approved | 5 Permanent rejected), reject_reason_id, reject_reason_text
  GET_SINGLE_USER_BY_PROFILE: 'users/get-users-by-profile', // ?role_id=X&mobile_no=X
  DOWNLOAD_USER_EXCEL: 'users/download-user-excel', // Append: &role_id=X
  DOWNLOAD_SELECTED_USERS: 'users/download-userdata', // POST with body
  IMPORT_USERS: 'users/import-users', // POST FormData
  IMPORT_USERS_ROLEWISE: 'users/import-userdetails-rolewise', // POST FormData (excel_data, zip_file, role_id, program_id)
  GET_USER_DETAIL_UPDATE_TYPES: 'hubadmin/user-detail-update-types', // GET ?program_id=1
  UPDATE_USER_DETAILS: 'hubadmin/update-user-details', // POST FormData (program_id, type, update_mode, excel_file, proof_upload)
  GET_ENROLLMENT_HEADERS: 'users/get-userenrollment-headers', // GET - Get enrollment headers
  // Bulk Upload Endpoints
  BULK_UPLOAD: 'users/import-user-batch', // POST FormData (csv_file, role_id, program_id)
  GET_BATCH_FILES: 'users/get-user-batch', // GET - Get batch upload history
  // User Type & Group Workflow Endpoints
  GET_USER_TYPES: 'enrollment-onboarding/get-user-types', // GET ?program_id=X - Program user types/roles
  GET_USER_GROUPS: 'enrollment-onboarding/get-user-groups', // GET ?user_type_id=X&program_id=1
  GET_GROUPS_BY_ROLE: 'users/geo-groups-by-role', // GET ?role_id=X - Get groups for selected role
  GET_ENROLLMENT_FIELDS: 'users/enrollment-fields', // GET ?enrollment_id=X&role_id=X - Get fields for Excel template
  IMPORT_USER: 'users/import-user', // POST FormData (role_id, enrollment_id, csv_file) - Upload user file
  EXPORT_USERS:
    'users/export-users', // GET ?program_id=&mobile=&state_id=&city_id=&search=&enrollment_setting_id=&status=&region_id=&role_id=
  GET_GEO_HIERARCHY: 'geo/get-geo-hierarchy', // GET ?program_id=1 - regions → states → cities
  GET_UPLOAD_HISTORY: `${API_BASE_URL}queue/file-progress-status-module-wise`, // GET ?module_type=enrollment_onboarding&program_id=1 - Get upload history for user imports
};

// Entity Endpoints
export const ENTITY_ENDPOINTS = {
  GET_ALL: 'users/get-entity-data', // Query params: ?search=X&flag=0 (flag=1 for export)
  GET_EXCEL_HEADERS: 'users/download-entity-excel', // GET - Get Excel template headers
  IMPORT_EXCEL: 'users/import-entity', // POST FormData (excel_data, program_id)
};

// User Supervisor Mapping Endpoints
export const USER_SUPERVISOR_MAPPING_ENDPOINTS = {
  GET_EXCEL_HEADERS: 'user-hierarchy/get-hierarchy-roles-for-excel', // GET - Get Excel template headers
  IMPORT_EXCEL: 'user-hierarchy/import-hierarchy-details', // POST FormData (excel_data, program_id)
  GET_DATA: `${API_BASE_URL}queue/file-progress-status-module-wise`, // GET - Get job status for user hierarchy imports
  GET_HIERARCHY_BY_MOBILE: 'user-hierarchy/get-hierarchy-by-mobile', // GET ?program_id=1&mobile=XXXXXXXXXX
};

// Deactivate Users Endpoints
export const DEACTIVATE_USERS_ENDPOINTS = {
  GET_EXCEL_HEADERS: 'users/download-hierarchy-excel',
  DOWNLOAD_USER_HIERARCHY_EXCEL: 'users/get-import-hierarchy',
  INACTIVE_USERS_UPLOAD: 'users/inactivate-hierarchy-data',
  GET_HIERARCHY_USER_LIST: 'users/get-hierarchy-id-details',
  INACTIVE_HIERARCHY_TOGGLE: 'users/inactivate-hierarchy',
};

// Supervisor Split and Assign Endpoints
export const SUPERVISOR_SPLIT_ASSIGN_ENDPOINTS = {
  GET_ROLES: 'user-role/list-roles',
  SUPERVISOR_SPLIT_ASSIGN: 'users/supervisor-splitand-assign',
};

// Reset Bank Details Endpoints
export const RESET_BANK_DETAILS_ENDPOINTS = {
  GET_USERS_WITH_BANK_DETAILS: 'hubadmin/list-bank-details', // GET - Query param: ?mobile_num=X
  RESET_BANK_ACCOUNT: 'hubadmin/reset-bank-account', // POST - Payload: { user_id, program_id }
};

// Bulk Deactivate End Role Endpoints
export const BULK_DEACTIVATE_END_ROLE_ENDPOINTS = {
  GET_EXCEL_HEADERS: 'users/bulk-hierarchy-inactivate-excel', // GET - Get Excel template headers
  UPLOAD_AND_DEACTIVATE: 'users/bulk-hierarchy-inactivate-only', // POST FormData - Upload and deactivate users
};
